import {Box} from "@mui/material";
import type {Theme} from "@mui/material";
import {
	GRID_CHECKBOX_SELECTION_COL_DEF,
	type GridColDef
} from "@mui/x-data-grid-pro";
import type {UserListDto} from "@/app2/shared/data";
import type {GetCohortSettingsResponse} from "@/app2/shared/data/gen";
import {convertSecondsToHHMM} from "@/utils";
import {formatJobCategoryLabel} from "@/utils/jobCategoryLabel";
import { JobCategory } from "@buildwitt/identity-client";
import {serverSideDateOnlyFilterOperators} from "@/app2/shared/dataGrid/serverSideDateTimeColumnFilters";
import {formatDateForDisplay} from "@/app2/shared/utils/dateManipulation";

/** Row shape for the admin users grid; API may add hire/title dates before codegen catches up. */
export type UsersListGridRow = UserListDto & {
	hireDate?: string | null;
	titleStartDate?: string | null;
};

/** `AdminTableColumnsPanel` reads `columnPanelGroup`; MUI types omit it by default. */
type UsersListColumnDef = GridColDef<UsersListGridRow> & {columnPanelGroup?: string};

function isCustomFieldActive(
	settings: GetCohortSettingsResponse | null | undefined,
	field: 1 | 2 | 3
): boolean {
	const key = `isActiveCustomField${field}` as keyof GetCohortSettingsResponse;
	return Boolean(settings?.[key]);
}

function customFieldHeader(
	settings: GetCohortSettingsResponse | null | undefined,
	field: 1 | 2 | 3,
	fallback: string
): string {
	const key = `customFieldName${field}` as keyof GetCohortSettingsResponse;
	const name = settings?.[key] as unknown;
	return typeof name === "string" && name.trim() !== "" ? name : fallback;
}

export const getUsersListHeadCells = (
	theme: Theme,
	cohortSettings?: GetCohortSettingsResponse | null
): UsersListColumnDef[] => {
	const baseColumns: UsersListColumnDef[] = [
		{
			...GRID_CHECKBOX_SELECTION_COL_DEF,
			type: "string" as const,
			hideable: false
		},
		{
			field: "firstname",
			flex: 1,
			headerName: "First Name",
			type: "string" as const,
			columnPanelGroup: "Identity",
			hideable: false
		},
		{
			field: "lastname",
			flex: 1,
			headerName: "Last Name",
			type: "string" as const,
			columnPanelGroup: "Identity",
			hideable: false
		},
		{
			field: "employeeCode",
			flex: 1,
			headerName: "Employee Code",
			type: "string" as const,
			columnPanelGroup: "Identity"
		},
		{
			field: "jobTitle",
			flex: 1,
			headerName: "Job Title",
			type: "string" as const,
			columnPanelGroup: "Role"
		},
		{
			field: "jobCategory",
			flex: 1,
			headerName: "Job Category",
			type: "string" as const,
			columnPanelGroup: "Role",
			valueGetter: (params) =>
				formatJobCategoryLabel(params.value as JobCategory | null) ?? ""
		},
		{
			field: "hireDate",
			flex: 1,
			type: "date" as const,
			headerName: "Hire Date",
			columnPanelGroup: "Role",
			filterOperators: serverSideDateOnlyFilterOperators,
			valueGetter: (params) =>
				params.row.hireDate ? new Date(params.row.hireDate as string) : null,
			valueFormatter: (params) => {
				if (!params.value) return "";
				return formatDateForDisplay(params.value as Date, {timeZone: "UTC"});
			}
		},
		{
			field: "titleStartDate",
			flex: 1,
			type: "date" as const,
			headerName: "Title Start Date",
			columnPanelGroup: "Role",
			filterOperators: serverSideDateOnlyFilterOperators,
			valueGetter: (params) =>
				params.row.titleStartDate ? new Date(params.row.titleStartDate as string) : null,
			valueFormatter: (params) => {
				if (!params.value) return "";
				return formatDateForDisplay(params.value as Date, {timeZone: "UTC"});
			}
		},
		{
			field: "division",
			flex: 1,
			headerName: "Division",
			type: "string" as const,
			columnPanelGroup: "Role"
		},
		{
			field: "supervisorName",
			flex: 1,
			headerName: "Supervisor",
			type: "string" as const,
			columnPanelGroup: "Role"
		},
		{
			field: "streak",
			flex: 1,
			type: "number" as const,
			maxWidth: 110,
			headerName: "Streak",
			headerAlign: "left",
			align: "left",
			renderCell: (e) => {
				let textColor: string | undefined;
				switch (true) {
					case e.value === 0:
						textColor = theme.palette.error.main;
						break;
					case e.value > 0 && e.value <= 4:
						textColor = theme.palette.warning.main;
						break;
					case e.value > 4:
						textColor = theme.palette.success.main;
						break;
				}
				return (
					<Box color={textColor} component="span">
						{e.value}
					</Box>
				);
			},
			columnPanelGroup: "Learning"
		},
		{
			field: "completedModules",
			flex: 1,
			type: "number" as const,
			maxWidth: 110,
			headerAlign: "left",
			align: "left",
			headerName: "Completed Lessons",
			columnPanelGroup: "Learning"
		},
		{
			field: "overdue",
			flex: 1,
			type: "number" as const,
			maxWidth: 110,
			headerName: "Overdue Lessons",
			headerAlign: "left",
			align: "left",
			renderCell: (e) => {
				let textColor: string | undefined;
				switch (true) {
					case e.value === 0:
						textColor = theme.palette.success.main;
						break;
					case e.value > 0 && e.value <= 4:
						textColor = theme.palette.warning.main;
						break;
					case e.value > 4:
						textColor = theme.palette.error.main;
						break;
				}
				return (
					<Box color={textColor} component="span">
						{e.value}
					</Box>
				);
			},
			columnPanelGroup: "Learning"
		},
		{
			field: "timeSpentOnTraining",
			flex: 1,
			type: "number" as const,
			maxWidth: 110,
			headerName: "Total Watch Time (HH:MM)",
			headerAlign: "left",
			align: "left",
			filterable: false,
			valueGetter: (e) => (e.value ? convertSecondsToHHMM(e.value) : "0"),
			columnPanelGroup: "Learning"
		},
		{
			field: "email",
			flex: 1,
			headerName: "Email",
			type: "string" as const,
			columnPanelGroup: "Contact"
		},
		{
			field: "phone",
			flex: 1,
			headerName: "Phone",
			type: "string" as const,
			columnPanelGroup: "Contact"
		}
	];

	if (isCustomFieldActive(cohortSettings, 1)) {
		baseColumns.push({
			field: "customFieldValue1",
			flex: 1,
			headerName: customFieldHeader(cohortSettings, 1, "Custom Field 1"),
			type: "string" as const,
			columnPanelGroup: "Custom Fields"
		});
	}
	if (isCustomFieldActive(cohortSettings, 2)) {
		baseColumns.push({
			field: "customFieldValue2",
			flex: 1,
			headerName: customFieldHeader(cohortSettings, 2, "Custom Field 2"),
			type: "string" as const,
			columnPanelGroup: "Custom Fields"
		});
	}
	if (isCustomFieldActive(cohortSettings, 3)) {
		baseColumns.push({
			field: "customFieldValue3",
			flex: 1,
			headerName: customFieldHeader(cohortSettings, 3, "Custom Field 3"),
			type: "string" as const,
			columnPanelGroup: "Custom Fields"
		});
	}

	return baseColumns;
};
